/**
 * Every backend error has the same envelope, produced by AllExceptionsFilter:
 *   { statusCode, code, message, timestamp, path, details? }
 *
 * `message` is already a single string — class-validator arrays are joined with "; ".
 */

export interface ApiErrorBody {
  statusCode: number;
  code: string;
  message: string;
  timestamp: string;
  path: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  /** Seconds until the rate limit resets, from the Retry-After header. */
  readonly retryAfter: number | null;

  constructor(body: ApiErrorBody, retryAfter: number | null = null) {
    super(body.message);
    this.name = 'ApiError';
    this.status = body.statusCode;
    this.code = body.code;
    this.details = body.details;
    this.retryAfter = retryAfter;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  /**
   * Refresh failure comes in three flavours (invalid / reused / expired) and the backend
   * gives all three `code: "UNAUTHORIZED"` — only the message distinguishes them.
   * Reuse additionally revokes the whole token family server-side, so every device is
   * logged out. Worth telling the user why, rather than a bare "session expired".
   */
  get isTokenReuse(): boolean {
    return this.status === 401 && /reuse/i.test(this.message);
  }
}

import { t } from '@/lib/i18n';
/** The request never reached the server (offline, DNS, connection refused). */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super(t('errors.unreachable'));
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export function isNetworkError(e: unknown): e is NetworkError {
  return e instanceof NetworkError;
}

/**
 * Uma frase para o usuário, a partir de qualquer coisa lançada.
 *
 * As mensagens de domínio do backend são escritas para gente e passam adiante — **em inglês**, que
 * é o idioma em que o servidor responde hoje. Um usuário em português vê "Invalid credentials"
 * vindo daqui; é dívida conhecida, e fechá-la exige `Accept-Language` no backend.
 *
 * Mensagem de framework não passa: um `ThrottlerException` chega como "ThrottlerException: Too Many
 * Requests", e um 500 em desenvolvimento carrega o erro interno cru. Essas são substituídas.
 */
export function errorMessage(e: unknown): string {
  if (isNetworkError(e)) return t('errors.network');

  if (isApiError(e)) {
    if (e.isRateLimited) {
      return e.retryAfter
        ? t('errors.rateLimited', { seconds: e.retryAfter })
        : t('errors.rateLimitedSoon');
    }
    if (e.status >= 500) return t('errors.server');
    // Anything that reads like a framework class name is not for the user.
    if (/^[A-Z][A-Za-z]*(Exception|Error):/.test(e.message)) {
      return t('errors.generic');
    }
    return e.message;
  }

  if (e instanceof Error && e.message) return e.message;
  return t('errors.generic');
}
